import React, { useMemo, useCallback } from 'react';
import { Eye } from 'lucide-react';
import BiographySection from './BiographySection';
import EditableContent from './EditableContent';
import { BiographyField } from './types';
import { CharacterData } from '../../types/character';

interface AppearanceSectionProps {
  character: CharacterData;
  updateCharacterData: (updates: any) => void;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * AppearanceSection - Specialized section for character appearance
 * Uses BiographySection + EditableContent composition
 */
const AppearanceSection: React.FC<AppearanceSectionProps> = ({
  character,
  updateCharacterData,
  defaultExpanded = true,
  priority = 'high'
}) => {
  // Convert character appearance data to biography fields
  // These match the selections from Character Creation wizard
  const fields = useMemo((): BiographyField[] => {
    const appearance = character.biography?.appearance || {};

    return [
      {
        id: 'clothes',
        label: 'Clothes',
        value: appearance.clothes || '',
        type: 'text',
        editable: true,
        placeholder: 'e.g., practical, haphazard, pristine...',
        maxLength: 100,
        helpText: 'Selected from character creation'
      },
      {
        id: 'eyes',
        label: 'Eyes',
        value: appearance.eyes || '',
        type: 'text',
        editable: true,
        placeholder: 'e.g., carnations, earth, fire...',
        maxLength: 100,
        helpText: 'Selected from character creation'
      },
      {
        id: 'body',
        label: 'Body',
        value: appearance.body || '',
        type: 'text',
        editable: true,
        placeholder: 'e.g., broad, lanky, stocky...',
        maxLength: 100,
        helpText: 'Selected from character creation'
      },
      {
        id: 'skin',
        label: 'Skin',
        value: appearance.skin || '',
        type: 'text',
        editable: true,
        placeholder: 'e.g., ashes, rose, obsidian...',
        maxLength: 100,
        helpText: 'Selected from character creation'
      },
      {
        id: 'attitude',
        label: 'Attitude',
        value: appearance.attitude || '',
        type: 'text',
        editable: true,
        placeholder: 'e.g., a loose cannon, a protector...',
        maxLength: 100,
        helpText: 'Selected from character creation'
      }
    ];
  }, [character.biography?.appearance]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldId: string, value: string | string[]) => {
    const updates = {
      biography: {
        ...character.biography,
        appearance: {
          ...character.biography?.appearance,
          [fieldId]: value
        }
      }
    };
    updateCharacterData(updates);
  }, [character.biography, updateCharacterData]);

  // Handle save (for any additional validation or processing)
  const handleSave = useCallback(async () => {
    // Additional save logic if needed
    console.log('Appearance data saved');
  }, []);

  // Never mark as empty - users should always be able to edit appearance fields
  const isEmpty = false;

  return (
    <BiographySection
      sectionId="bio-appearance"
      title="Appearance & Traits"
      icon={<Eye size={18} />}
      defaultExpanded={defaultExpanded}
      priority={priority}
      isEmpty={isEmpty}
      emptyMessage="No appearance details recorded yet"
    >
      <EditableContent
        fields={fields}
        onFieldChange={handleFieldChange}
        onSave={handleSave}
        maxFieldsPerRow={2}
        showLabels={true}
        useCard={false}
      />
    </BiographySection>
  );
};

export default AppearanceSection;